import React from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

interface DataTableProps {
  data: any[];
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  stateName?: string;
}

// Define the main columns to display first
const CORE_COLUMNS = [
  'Division', 'Date', 'Time', 'HomeTeam', 'AwayTeam', 'FTHG', 'FTAG', 'FTR',
  'HTHG', 'HTAG', 'HTR', 'Referee', 'HS', 'AS', 'HST', 'AST', 'HF',
  'AF', 'HC', 'AC', 'HY', 'AY', 'HR', 'AR'
];

// Define the additional betting columns - these will be shown after the core columns
const BETTING_COLUMNS = [
  // Match odds
  'B365H', 'B365D', 'B365A', 'BWH', 'BWD', 'BWA', 'BFH', 'BFD', 'BFA', 'PSH', 'PSD', 'PSA',
  'WHH', 'WHD', 'WHA', '1XBH', '1XBD', '1XBA', 'MaxH', 'MaxD', 'MaxA', 'AvgH', 'AvgA',
  'BFEH', 'BFED', 'BFEA',
  // Over/under odds
  'B365>2.5', 'B365<2.5', 'P>2.5', 'P<2.5', 'Max>2.5', 'Max<2.5', 'Avg>2.5', 'Avg<2.5', 'BFE>2.5', 'BFE<2.5',
  // Asian handicap
  'AHh', 'B365AHH', 'B365AHA', 'PAHH', 'PAHA', 'MaxAHH', 'MaxAHA', 'AvgAHH', 'AvgAHA', 'BFEAHH', 'BFEAHA',
  // Closing odds
  'B365CH', 'B365CD', 'B365CA', 'BWCH', 'BWCD', 'BWCA', 'BFCH', 'BFCD', 'BFCA', 'PSCH', 'PSCD', 'PSCA',
  'WHCH', 'WHCD', 'WHCA', '1XBCH', '1XBCD', '1XBCA', 'MaxCH', 'MaxCD', 'MaxCA', 'AvgCH', 'AvgCD', 'AvgCA',
  'BFECH', 'BFECD', 'BFECA',
  // Closing over/under
  'B365C>2.5', 'B365C<2.5', 'PC>2.5', 'PC<2.5', 'MaxC>2.5', 'MaxC<2.5', 'AvgC>2.5', 'AvgC<2.5', 'BFEC>2.5', 'BFEC<2.5',
  // Closing Asian handicap
  'AHCh', 'B365CAHH', 'B365CAHA', 'PCAHH', 'PCAHA', 'MaxCAHH', 'MaxCAHA', 'AvgCAHH', 'AvgCAHA', 'BFECAHH', 'BFECAHA'
];

// Combine all columns
const COLUMN_ORDER = [...CORE_COLUMNS, ...BETTING_COLUMNS];

const DataTable: React.FC<DataTableProps> = ({
  data,
  onSort,
  sortColumn,
  sortDirection,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No match data available</p>
      </div>
    );
  }

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  const renderCell = (row: any, column: string) => {
    if (column === 'HomeTeam' || column === 'AwayTeam') {
      return (
        <Link 
          to={`/team/${row[column]}`}
          className="text-blue-600 hover:text-blue-800 hover:underline"
        >
          {row[column]}
        </Link>
      );
    }
    
    if (column === 'Date') {
      // Keep date exactly as stored
      return row[column];
    }

    // Handle numeric values for betting odds
    if (BETTING_COLUMNS.includes(column)) {
      // Check if the value exists and is numeric
      if (row[column] !== undefined && row[column] !== null && !isNaN(row[column])) {
        // Format to 2 decimal places
        return Number(row[column]).toFixed(2);
      }
      return ''; // Return empty string for undefined betting odds
    }

    return row[column];
  };

  // Determine which columns are actually present in the data
  const availableColumns = COLUMN_ORDER.filter(column => 
    data.some(row => row[column] !== undefined && row[column] !== null)
  );

  // Debug log to check the data being passed to DataTable
  console.log('DataTable received data:', data);
  console.log('Available columns:', availableColumns);

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {availableColumns.map((column) => (
              <TableHead key={column} className="font-bold text-black bg-gray-100 whitespace-nowrap">
                <Button
                  variant="ghost"
                  onClick={() => onSort(column)}
                  className="flex items-center hover:bg-gray-200"
                >
                  {column} {renderSortIcon(column)}
                </Button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={`${row.HomeTeam}-${row.AwayTeam}-${row.Date}-${index}`}>
              {availableColumns.map((column) => (
                <TableCell key={`${row.HomeTeam}-${row.AwayTeam}-${row.Date}-${index}-${column}`} className="whitespace-nowrap">
                  {renderCell(row, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default DataTable;
